import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { IoTrashOutline } from "react-icons/io5";
import {
  MdOutlineContentCopy,
  MdOutlineReply,
  MdOutlineStarBorder,
  MdStar,
} from "react-icons/md";
import { HiOutlinePencil, HiOutlineArrowDownTray } from "react-icons/hi2";
import { RiShareForwardLine } from "react-icons/ri";
import { TbPinned, TbPinnedOff } from "react-icons/tb";
import { extractFirstUrl } from "../../../../utils/isLink";
import {
  addMessage,
  updateMessageOptions,
} from "../../../../store/Slices/MessageOptionsSlice";
import { Message } from "../../../../types/MessageListType";
import useApiPost from "../../../../hooks/PostData";
import { updateIsStarMessage } from "../../../../store/Slices/MessageListSlice";
import toast from "react-hot-toast";
import { updateSendMessageData } from "../../../../store/Slices/SendMessageSlice";
import { useStarMessageList } from "../../../../store/api/useStarMessageList";
import { useTranslateText } from "../../../../hooks/useTranslateText";
import { displayStoredUploadFilename } from "../../../../utils/displayUploadFilename";
import { downloadFileDirect } from "../../../../utils/fileDownload";
import { resolveMediaSrc } from "../../../../utils/mediaMessageUrl";

type OpenBubbleMenu = {
  message: Message;
  x: number;
  y: number;
};

type OpenListener = (menu: OpenBubbleMenu) => void;
let openListener: OpenListener | null = null;

function openBubbleMenu(menu: OpenBubbleMenu) {
  openListener?.(menu);
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text);
}

const itemClassName =
  "flex w-full items-center justify-between gap-3 rounded-lg px-4 py-2 text-left text-sm text-darkText hover:bg-dropdownOptionHover";
const iconClassName = "shrink-0 text-lg text-lightText";

/**
 * One menu for every bubble, portaled to body.
 * rctx closed it on the same right-click (document contextmenu / mousedown).
 */
export function MessageBubbleContextMenuHost() {
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.userData);
  const pinMessageData = useAppSelector(
    (state) => state.MessageList.pinned_messages.Records,
  );
  const { postData } = useApiPost();
  const { refetch } = useStarMessageList();
  const translate = useTranslateText();
  const [menu, setMenu] = useState<OpenBubbleMenu | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    openListener = setMenu;
    return () => {
      if (openListener === setMenu) openListener = null;
    };
  }, []);

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!menu || !el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let top = menu.y;
    let left = menu.x;
    if (top + rect.height > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - rect.height - pad);
    }
    if (left + rect.width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - rect.width - pad);
    }
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    let scrollArmed = false;
    const onPointerDown = (event: PointerEvent) => {
      if (event.button === 2) return;
      const target = event.target;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onScroll = (event: Event) => {
      if (!scrollArmed) return;
      const target = event.target;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      close();
    };
    // Attach after the opening right-click finishes, so that gesture does not close the menu.
    const timer = window.setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDown, true);
      document.addEventListener("keydown", onKey);
      window.addEventListener("scroll", onScroll, true);
    }, 0);
    const armScroll = window.setTimeout(() => {
      scrollArmed = true;
    }, 400);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(armScroll);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menu]);

  if (!menu) return null;

  const messageData = menu.message;
  const close = () => setMenu(null);
  const isOwn =
    messageData.User?.user_id == userData?.user_id ||
    messageData.sender_id == userData?.user_id;
  const deletedFor = Array.isArray(messageData.deleted_for)
    ? messageData.deleted_for
    : [];
  const isDeleted =
    messageData.deleted_for_everyone ||
    deletedFor.includes(userData?.user_id) ||
    deletedFor.includes(userData?.user_id?.toString?.());
  const canEdit =
    isOwn &&
    !isDeleted &&
    (messageData.message_type === "text" || messageData.message_type === "link");
  const contentText = String(messageData.message_content || "").trim();
  const extractedUrl = extractFirstUrl(contentText);
  const textWithoutUrl = extractedUrl
    ? contentText.replace(extractedUrl, "").trim()
    : contentText;
  const canCopyText =
    !isDeleted && messageData.message_type === "text" && textWithoutUrl.length > 0;
  const canCopyLink =
    !isDeleted &&
    !!extractedUrl &&
    (messageData.message_type === "link" || messageData.message_type === "text");
  const isPinned = (pinMessageData || []).some(
    (pin) => pin.message_id === messageData.message_id,
  );
  const docUrl =
    messageData.message_type === "doc" ? resolveMediaSrc(messageData) : "";

  async function starMessage(message_id: number, remove_from_star: boolean) {
    await postData("chat/star-unstar-message", {
      message_id: String(message_id),
    });
    refetch();
    toast.success(
      `Message  ${remove_from_star ? "removed from" : "added to"} star  `,
    );
    dispatch(
      updateIsStarMessage({
        message_id,
        is_star_message: !remove_from_star,
      }),
    );
  }

  async function unpinMessage() {
    await postData("chat/pin-unpin-message", {
      message_id: messageData.message_id,
      pin_lifetime: -1,
    });
    toast.success("Message Removed from Pin");
  }

  const run = (action: () => void) => {
    close();
    action();
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-[200] w-52 rounded-xl border border-borderColor bg-modalBg p-1 shadow-lg"
      style={{ top: menu.y, left: menu.x }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <button
        type="button"
        role="menuitem"
        className={itemClassName}
        onClick={() =>
          run(() => {
            if (isPinned) {
              void unpinMessage();
            } else {
              dispatch(updateMessageOptions({ show_pin_message_modal: true }));
              dispatch(addMessage(messageData));
            }
          })
        }
      >
        <span>{translate(isPinned ? "Unpin" : "Pin")}</span>
        {isPinned ? (
          <TbPinnedOff className={iconClassName} />
        ) : (
          <TbPinned className={iconClassName} />
        )}
      </button>

      {canEdit ? (
        <button
          type="button"
          role="menuitem"
          className={itemClassName}
          onClick={() =>
            run(() => {
              dispatch(
                updateSendMessageData({
                  edit_message_id: messageData.message_id,
                  edit_message_original: String(messageData.message_content || ""),
                  message: messageData.message_content,
                  message_type: "text",
                  reply_to: 0,
                }),
              );
            })
          }
        >
          <span>{translate("Edit")}</span>
          <HiOutlinePencil className={iconClassName} />
        </button>
      ) : null}

      {canCopyText ? (
        <button
          type="button"
          role="menuitem"
          className={itemClassName}
          onClick={() =>
            run(() => {
              void copyToClipboard(contentText)
                .then(() => toast.success("Copied"))
                .catch(() => toast.error("Failed to copy"));
            })
          }
        >
          <span>{translate("Copy")}</span>
          <MdOutlineContentCopy className={iconClassName} />
        </button>
      ) : null}

      {canCopyLink && extractedUrl ? (
        <button
          type="button"
          role="menuitem"
          className={itemClassName}
          onClick={() =>
            run(() => {
              void copyToClipboard(extractedUrl)
                .then(() => toast.success("Copied"))
                .catch(() => toast.error("Failed to copy"));
            })
          }
        >
          <span>{translate("Copy Link")}</span>
          <MdOutlineContentCopy className={iconClassName} />
        </button>
      ) : null}

      <button
        type="button"
        role="menuitem"
        className={itemClassName}
        onClick={() =>
          run(() => {
            dispatch(
              updateSendMessageData({
                reply_to: messageData.message_id,
                edit_message_id: 0,
                edit_message_original: "",
              }),
            );
          })
        }
      >
        <span>{translate("Reply")}</span>
        <MdOutlineReply className={iconClassName} />
      </button>

      <button
        type="button"
        role="menuitem"
        className={itemClassName}
        onClick={() =>
          run(() => {
            dispatch(
              updateMessageOptions({
                selectMessage: true,
                forward_message: true,
              }),
            );
            dispatch(addMessage(messageData));
          })
        }
      >
        <span>{translate("Forward")}</span>
        <RiShareForwardLine className={iconClassName} />
      </button>

      <button
        type="button"
        role="menuitem"
        className={itemClassName}
        onClick={() =>
          run(() => {
            void starMessage(messageData.message_id, !!messageData.starred);
          })
        }
      >
        <span>{translate(messageData.starred ? "Unstar" : "Star")}</span>
        {messageData.starred ? (
          <MdStar className="shrink-0 text-lg text-themeColor" />
        ) : (
          <MdOutlineStarBorder className={iconClassName} />
        )}
      </button>

      {docUrl ? (
        <button
          type="button"
          role="menuitem"
          className={itemClassName}
          onClick={() =>
            run(() => {
              const filename = displayStoredUploadFilename(
                docUrl,
                messageData.message_length || undefined,
              );
              downloadFileDirect(docUrl, filename);
            })
          }
        >
          <span>{translate("Download") || "Download"}</span>
          <HiOutlineArrowDownTray className={iconClassName} />
        </button>
      ) : null}

      <button
        type="button"
        role="menuitem"
        className={`${itemClassName} !text-[#FF3B30]`}
        onClick={() =>
          run(() => {
            dispatch(
              updateMessageOptions({
                selectMessage: true,
                delete_message: true,
                delete_only_from_me:
                  messageData.deleted_for_everyone ||
                  deletedFor.includes(userData?.user_id?.toString?.()) ||
                  messageData.User?.user_id != userData?.user_id,
              }),
            );
            dispatch(addMessage(messageData));
          })
        }
      >
        <span>{translate("Delete")}</span>
        <IoTrashOutline className="shrink-0 text-lg" />
      </button>
    </div>,
    document.body,
  );
}

/**
 * Per-bubble trigger only — does not mount its own menu.
 */
export default function SelectedMessageOption({
  messageData,
  children,
  disabled = false,
}: {
  messageData: Message;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div
      className="w-fit max-w-full"
      onContextMenu={(event) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();
        openBubbleMenu({
          message: messageData,
          x: event.clientX,
          y: event.clientY,
        });
      }}
    >
      {children}
    </div>
  );
}
